/* eslint-disable unicorn/filename-case -- TODO rename later */
import { IstariClient } from "@istari/istari-client";

import AuthenticationStore from "../auth";
import { getEnv } from "../utils/get_env";

const getClientSDK = () => {
  const AS = AuthenticationStore();
  return new IstariClient(getEnv("VITE_FILE_AUTH_ENDPOINT") || "http://localhost:8000", AS.getAccessToken() || "");
};

/**
 * Detects if we're running in a test environment by checking for vitest globals
 */
const isTestEnvironment = () => {
  return (
    typeof (globalThis as { vi?: unknown; describe?: unknown }).vi !== "undefined" ||
    typeof (globalThis as { vi?: unknown; describe?: unknown }).describe !== "undefined"
  );
};

/**
 * PROBLEM: Race condition causing 403 errors on initial app load
 *
 * Previously, clientSDK was statically initialized at module load time:
 * `const clientSDK = new IstariClient(endpoint, AS.getAccessToken() || "")`
 *
 * This caused a race condition where:
 * 1. Module loads and clientSDK is created with empty/expired token (user not authenticated yet)
 * 2. React components mount and immediately make API calls using clientSDK
 * 3. All API calls fail with 403 Forbidden because token is empty/invalid
 * 4. User had to refresh page after authentication to get valid token
 *
 * SOLUTION: Dynamic token retrieval via Proxy (production) + Static instance (tests)
 *
 * In production/development:
 * The Proxy intercepts every property access on clientSDK and:
 * 1. Creates a fresh IstariClient instance with the CURRENT auth token
 * 2. Properly binds method calls to the fresh client instance
 * 3. Ensures every API call gets the latest valid authentication token
 *
 * In test environment:
 * Uses a static IstariClient instance that can be properly mocked with vi.spyOn
 *
 * This eliminates the race condition and ensures all API calls work correctly
 * from the moment the user is authenticated, without requiring page refreshes.
 */
const clientSDK = isTestEnvironment()
  ? getClientSDK() // Static instance for tests - can be spied on
  : new Proxy({} as IstariClient, {
      get(target, prop) {
        const client = getClientSDK();
        const value = client[prop as keyof IstariClient];

        if (typeof value === "function") {
          return value.bind(client);
        }

        return value;
      },
    });

export default clientSDK;
